/**
 * Text Protection Utilities
 * Prevents copying, selecting, and extracting text content
 */

/**
 * Disable text selection globally
 */
export const disableTextSelection = () => {
  document.addEventListener('selectstart', (e) => {
    if (!e.target || typeof e.target.closest !== 'function') return;
    if (e.target.closest('.protected-text') || e.target.classList.contains('protected-text')) {
      e.preventDefault();
      return false;
    }
  });
};

/**
 * Disable copy/paste for protected text
 */
export const disableCopyPaste = () => {
  document.addEventListener('copy', (e) => {
    if (!e.target || typeof e.target.closest !== 'function') return;
    if (e.target.closest('.protected-text') || e.target.classList.contains('protected-text')) {
      e.preventDefault();
      e.clipboardData.setData('text/plain', '');
      return false;
    }
  });

  document.addEventListener('cut', (e) => {
    if (!e.target || typeof e.target.closest !== 'function') return;
    if (e.target.closest('.protected-text') || e.target.classList.contains('protected-text')) {
      e.preventDefault();
      e.clipboardData.setData('text/plain', '');
      return false;
    }
  });
};

/**
 * Disable keyboard shortcuts for text operations
 */
export const disableTextKeyboardShortcuts = () => {
  document.addEventListener('keydown', (e) => {
    if (!e.target || typeof e.target.closest !== 'function') return;
    const isProtected = e.target.closest('.protected-text') || 
                       e.target.classList.contains('protected-text') ||
                       document.getSelection().toString().length > 0;

    if (isProtected) {
      // Prevent Ctrl+C (Copy)
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        e.preventDefault();
        return false;
      }
      // Prevent Ctrl+A (Select All)
      if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        e.preventDefault();
        return false;
      }
      // Prevent Ctrl+X (Cut)
      if ((e.ctrlKey || e.metaKey) && e.key === 'x') {
        e.preventDefault();
        return false;
      }
    }
  });
};

/**
 * Clear selection when clicking outside protected text
 */
export const clearSelectionOnClick = () => {
  document.addEventListener('mouseup', () => {
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) {
      const range = selection.getRangeAt(0);
      const container = range.commonAncestorContainer;
      const element = container.nodeType === 3 ? container.parentNode : container;
      
      if (element.closest('.protected-text') || element.classList.contains('protected-text')) {
        selection.removeAllRanges();
      }
    }
  });
};

/**
 * Obfuscate text in DOM
 * Makes it harder to extract from view-source or DevTools
 */
export const obfuscateText = (text) => {
  if (typeof text !== 'string') return text;
  
  // Convert to array of character codes
  const codes = text.split('').map(char => char.charCodeAt(0));
  
  // Return as data attribute format
  return {
    obfuscated: true,
    data: codes,
    render: () => {
      return text.split('').map((char, i) => ({
        char,
        code: codes[i]
      }));
    }
  };
};

/**
 * Protect specific text element
 */
export const protectTextElement = (element) => {
  if (!element) return;
  
  element.classList.add('protected-text');
  element.style.userSelect = 'none';
  element.style.WebkitUserSelect = 'none';
  element.style.MozUserSelect = 'none';
  element.style.msUserSelect = 'none';
  element.style.WebkitTouchCallout = 'none';
  
  // Prevent context menu
  element.addEventListener('contextmenu', (e) => {
    e.preventDefault();
    return false;
  });
  
  // Prevent copy
  element.addEventListener('copy', (e) => {
    e.preventDefault();
    e.clipboardData.setData('text/plain', '');
    return false;
  });
  
  // Prevent selection
  element.addEventListener('selectstart', (e) => {
    e.preventDefault();
    return false;
  });
};

/**
 * Monitor for text extraction attempts
 */
export const monitorTextExtraction = (callback) => {
  let extractionAttempts = 0;
  
  const detectExtraction = () => {
    const selection = window.getSelection();
    if (selection && selection.toString().length > 50) {
      const element = selection.getRangeAt(0).commonAncestorContainer;
      const parentElement = element.nodeType === 3 ? element.parentNode : element;
      
      if (parentElement.closest('.protected-text') || parentElement.classList.contains('protected-text')) {
        extractionAttempts++;
        if (callback) callback(extractionAttempts);
        
        // Clear selection
        selection.removeAllRanges();
      }
    }
  };
  
  document.addEventListener('selectionchange', detectExtraction);
  
  return () => {
    document.removeEventListener('selectionchange', detectExtraction);
  };
};

/**
 * Initialize all text protection measures
 */
export const initializeTextProtection = () => {
  disableTextSelection();
  disableCopyPaste();
  disableTextKeyboardShortcuts();
  clearSelectionOnClick();
  
  // Monitor for extraction attempts (silent)
  const cleanup = monitorTextExtraction(() => {
    // Silent monitoring - no alerts
  });
  
  return cleanup;
};

/**
 * Add invisible characters to text
 * Makes copied text identifiable (watermarking)
 */
export const watermarkText = (text, identifier = '') => {
  if (typeof text !== 'string') return text;
  
  // Add zero-width characters as watermark
  const zeroWidthSpace = '\u200B';
  const zeroWidthNonJoiner = '\u200C';
  const zeroWidthJoiner = '\u200D';
  
  // Encode identifier in zero-width characters
  const watermark = identifier.split('').map(char => {
    const code = char.charCodeAt(0);
    if (code % 3 === 0) return zeroWidthSpace;
    if (code % 3 === 1) return zeroWidthNonJoiner;
    return zeroWidthJoiner;
  }).join('');
  
  // Insert watermark at random positions
  const words = text.split(' ');
  const watermarkedWords = words.map((word, index) => {
    if (index % 3 === 0 && watermark[index]) {
      return word + watermark[index];
    }
    return word;
  });
  
  return watermarkedWords.join(' ');
};

/**
 * Detect if text was copied from protected area
 */
export const detectWatermark = (text) => {
  const zeroWidthChars = /[\u200B\u200C\u200D]/g;
  return zeroWidthChars.test(text);
};

/**
 * Replace text with non-copyable version
 */
export const makeTextNonCopyable = (element) => {
  if (!element) return;
  
  const originalText = element.textContent;
  element.textContent = '';
  
  // Create individual spans for each character
  originalText.split('').forEach((char, index) => {
    const span = document.createElement('span');
    span.textContent = char;
    span.setAttribute('data-index', index);
    span.style.display = 'inline';
    span.style.userSelect = 'none';
    element.appendChild(span);
  });
  
  protectTextElement(element);
};

export default {
  disableTextSelection,
  disableCopyPaste,
  disableTextKeyboardShortcuts,
  clearSelectionOnClick,
  obfuscateText,
  protectTextElement,
  monitorTextExtraction,
  initializeTextProtection,
  watermarkText,
  detectWatermark,
  makeTextNonCopyable
};
